import { useAppContextController } from "context/AppContext";
import { useEventContext } from "context/EventContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";

import { Card, Grid, Icon, Stack } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DataTable from "components/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useCompanyCache from "hooks/useCompanyCache";
import useVenueCache from "hooks/useVenueCache";
import PropTypes from "prop-types";

import fetchCustomers from "api/companies/fetchCustomers";
import CompanyLogoSquare from "components/CompanyLogoSquare";
import EventDate from "components/EventDate";
import fetchEvents from "layouts/pages/events/actions/fetchEvents";
import fetchSingleEvent from "layouts/pages/events/actions/fetchSingleEvent";
import fetchVenues from "layouts/pages/venues/actions/fetchVenues";

import FiltersList from "components/FiltersList";
import Searchbar from "components/Searchbar";
import VenueIcon from "components/VenueIcon";
import useSort from "utils/useSort";
// import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  generateApplicantInterviewChartReport,
  generateApplicantInterviewTableReport,
} from "api/reports/applicantReport";
import {
  generateEmployeeEventsChartReport,
  generateEmployeeEventsTableReport,
} from "api/reports/employeeReport";
import {
  generateCompanyInterviewChartReport,
  generateCompanyInterviewTableReport,
  generateEventChartReport,
  generateEventTableReport,
  generateVenueEventChartReport,
  generateVenueEventTableReport,
} from "api/reports/eventReport";
import { exportReport } from "api/reports/exportReport";
import { saveReport } from "api/reports/saveReport";
import DatePeriodSelector from "components/DatePeriodSelector";
import EventNumbers from "components/EventNumbers";
import PayrollModal from "components/PayrollModal";
import { defaultReportModalValues } from "components/ReportModal/fixtures";
import TimeInOutModal from "components/TimeInOutModal";
import useHandleBackButtonSubGrid from "hooks/useHandleBackButtonSubGrid";
import usePreferredPageSize from "hooks/usePreferredPageSize";
import useSessionAuth from "hooks/useSessionAuth";
import EventCalendar from "layouts/pages/events/components/EventCalendar";
import fetchSingleJob from "layouts/pages/jobs/actions/fetchSingleJob";
import { cloneDeep } from "lodash";
import moment from "moment";
import { getPeriodForDate } from "utils/helpers/payrollHelper";
import fetchEventsJobs from "../../actions/fetchEventsJobs";
import PayrollTableActions from "../PayrollTableActions";

const useStyle = makeStyles({
  box: {
    marginTop: 20,
    overflow: "visible!important",
  },
  addButton: {
    fontSize: 40,
  },
});

const PayrollTable = ({
  // eventUrl,
  navigateToAction,
  title = "Payroll",
  filters,
  setFilters,
  fetchAll,
  setActionSelected,
  showFiltersList = true,
  showSearchBar = true,
  showEventStatus = false,
  setOpen,
  setEventPreview,
  setJobPreview,
  // setInterviewPreview,
  defaultSort,
  setOpenEventPreview,
  isMobile,
  bindRefetchEvents,
  setToastAlert,
  // applicantId,
  // isEmployeeEvents,
  // isApplicantInterviews,
  // isInterview,
  name,
  parentPage,
  parentNavigateWithBack,
  parentNavigateToScreenWithBack,
  bindNavigateFunc,
  currentPeriod,
  setCurrentPeriod,
}) => {
  const [isMobileWidth, setIsMobileWidth] = useState(false);
  const { setRefetchEventsTable, setEvents, setEventsTableFilter } = useEventContext();
  const {
    venues,
    setVenues,
    currentEvent,
    setCurrentEvent,
    currentJob,
    setCurrentJob,
    currentLoggedUser,
    currentApplicant,
    setCurrentApplicant,
    userType,
    company,
    setCompany,
    setCompanyType,
    currentCompany,
    setCurrentCompany,
  } = useAppContextController();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsMobileWidth(true);
      } else {
        setIsMobileWidth(false);
      }
    };
    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const classes = useStyle();

  const { isLoadingCompany, refetchCompany } = useCompanyCache({
    company,
    setCompany,
    setCompanyType,
  });
  const { isLoadingVenues, refetchVenues } = useVenueCache({ venues, setVenues, company });
  const navigate = useNavigate();
  const [calendarListView, setCalendarListView] = useState("List");
  const [openReportMessageModal, setOpenReportMessageModal] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const { limit, setLimit, pageLimitConfirmationModal } = usePreferredPageSize(5);
  const [userVenueList, setUserVenueList] = useState([]);
  const [eventAdminVenueList, setEventAdminVenueList] = useState([]);
  const [isTimeModalOpen, toggleTimeModal] = useState(false);
  const [isPayrollModalOpen, togglePayrollModal] = useState(false);
  const { order, orderBy, toggleSort } = useSort();
  const { logoutAuth0User } = useSessionAuth();
  const isCompany = company?.companyType === "Company";
  const [lockedVenues, setLockedVenues] = useState([]);

  const isEventAdmin = currentLoggedUser?.employeeType === "Event Admin";

  const userAndEventAdminVenues = useMemo(
    () => Array.from(new Set([...userVenueList, ...eventAdminVenueList])),
    [eventAdminVenueList, userVenueList]
  );

  // Handle back button from parent screens
  const { navigateWithBack, navigateWithoutBack } = useHandleBackButtonSubGrid({
    parentPage,
    setPage,
    page,
    filters,
    navigateFunc: navigate,
    navigateWithBackFunc: parentNavigateWithBack,
  });

  // Handle back button to a different screen
  const navigateToScreenWithBack = useMemo(
    () =>
      parentNavigateToScreenWithBack
        ? (url) => parentNavigateToScreenWithBack(url, page, filters)
        : (url) => navigate(url),
    [filters, navigate, page, parentNavigateToScreenWithBack]
  );

  useEffect(() => {
    bindNavigateFunc?.(navigateWithBack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateWithBack]);

  const startOfPeriodDay = company?.payrollSettings?.payrollStartOfPeriod ?? 1;
  const periodLength = company?.payrollSettings?.payrollPeriodLength || "weekly";
  const shiftPeriodWeek = (company?.payrollSettings?.payrollShiftPeriodWeek || "No") === "Yes";

  const [anchorEl, setAnchorEl] = useState(null);
  const [dateRangeObj, setDateRangeObj] = useState(null);
  const startingDate = new Date();
  const currentDateCalendar = new Date();
  const [calendarMiddleDate, setCalendarMiddleDate] = useState(null);

  const [tableQueryDetails, setTableQueryDetails] = useState(null);
  const [chartQueryDetails, setChartQueryDetails] = useState(null);
  const [tableReportData, setTableReportData] = useState(null);
  const [chartReportData, setChartReportData] = useState(null);
  const [reportModalValues, setReportModalValues] = useState(defaultReportModalValues);

  const { mutateAsync: createEventTableReport } = useMutation(generateEventTableReport);
  const { mutateAsync: createEventChartReport } = useMutation(generateEventChartReport);
  const { mutateAsync: createEmployeeEventTableReport } = useMutation(
    generateEmployeeEventsTableReport
  );
  const { mutateAsync: createEmployeeEventChartReport } = useMutation(
    generateEmployeeEventsChartReport
  );
  const { mutateAsync: createVenueEventTableReport } = useMutation(generateVenueEventTableReport);
  const { mutateAsync: createVenueEventChartReport } = useMutation(generateVenueEventChartReport);
  const { mutateAsync: createCompanyInterviewTableReport } = useMutation(
    generateCompanyInterviewTableReport
  );
  const { mutateAsync: createCompanyInterviewChartReport } = useMutation(
    generateCompanyInterviewChartReport
  );
  const { mutateAsync: createApplicantInterviewTableReport } = useMutation(
    generateApplicantInterviewTableReport
  );
  const { mutateAsync: createApplicantInterviewChartReport } = useMutation(
    generateApplicantInterviewChartReport
  );
  const { mutateAsync: exportEventReport, isLoading: isLoadingExport } = useMutation(exportReport);
  const { mutateAsync: saveEventReport, isLoading: isLoadingSave } = useMutation(saveReport);

  const [chartTableSrc, setChartTableSrc] = useState(null);
  const [chartSrc, setChartSrc] = useState(null);
  const [selectedSubtables, setSelectedSubtables] = useState([]);

  const getLatestCriteria = (_tableReportData, _chartReportData, defaultValues) => {
    const tableCriteriaExists = _tableReportData?.criteria !== null;
    const chartCriteriaExists = _chartReportData?.criteria !== null;

    const tableTimestamp = tableCriteriaExists
      ? moment(_tableReportData.timestamp, "YYYY-MM-DDTHH:mm:ss.SSSZ")
      : null;
    const chartTimestamp = chartCriteriaExists
      ? moment(_chartReportData.timestamp, "YYYY-MM-DDTHH:mm:ss.SSSZ")
      : null;

    if (tableTimestamp && chartTimestamp) {
      return tableTimestamp.isAfter(chartTimestamp)
        ? _tableReportData.criteria
        : _chartReportData.criteria;
    }

    if (tableTimestamp && tableCriteriaExists) {
      return _tableReportData.criteria;
    }

    if (chartTimestamp && chartCriteriaExists) {
      return _chartReportData.criteria;
    }

    return defaultValues;
  };

  // Employee user only
  const handleClearVenueFilter = () => {
    setFilters((currFilt) => {
      const newFilters = {
        ...currFilt,
      };
      delete newFilters.venueSlug;
      delete newFilters.companySlug;
      return newFilters;
    });
  };

  const handleSearch = (entity) => {
    setCurrentEvent(null);
    setCurrentJob(null);

    const params = [];
    if (filters.entityType) params.push(`entityType=${filters.entityType}`);
    if (entity.slug) {
      if (company?.companyType === "Venue") params.push(`venueSlug=${entity.slug}`);
      else params.push(`companySlug=${entity.slug}`);
    }
    navigate(`/payroll${params.length ? `?${params.join("&")}` : ""}`);
  };

  const stringifiedFilters = JSON.stringify(filters ?? {});

  // reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [stringifiedFilters]);

  useEffect(() => {
    const setUserAdminVenues = () => {
      if (currentLoggedUser?.employeeType && currentLoggedUser?.clientOrgs?.length) {
        const venList = currentLoggedUser?.clientOrgs?.map((item) => item.slug);
        setEventAdminVenueList(venList || []);
      }
    };
    if (currentLoggedUser?.employeeType === "Event Admin" && currentLoggedUser?.clientOrgs) {
      setUserAdminVenues();
    }
  }, [
    currentLoggedUser?.employeeType,
    currentLoggedUser?.clientOrgs,
    currentLoggedUser?.venues,
    currentLoggedUser?.applicantId,
    venues,
    userType,
  ]);

  const options = useMemo(
    () =>
      fetchAll
        ? { fetchAll }
        : {
            page,
            limit,
            order,
            orderBy,
          },
    [fetchAll, limit, order, orderBy, page]
  );

  const allOptions = useMemo(() => ({ ...options, filters, venues }), [filters, options, venues]);

  const payrollFetchFunc = () => {
    const allOptionsForQuery = cloneDeep(allOptions);

    switch (allOptions?.filters?.entityType) {
      // case "Event":
      //   delete allOptionsForQuery.filters.entityType;
      //   allOptionsForQuery.filters.eventType = "Event";
      //   allOptionsForQuery.startDate = currentPeriod?.[0]?.toISOString();
      //   allOptionsForQuery.endDate = currentPeriod?.[1]?.toISOString();
      //   return fetchEvents(allOptionsForQuery);
      // case "Job":
      //   delete allOptionsForQuery.filters.entityType;
      //   allOptionsForQuery.filters.shiftJob = "Yes";
      //   allOptionsForQuery.shiftStartDate = currentPeriod?.[0]?.toISOString();
      //   allOptionsForQuery.shiftEndDate = currentPeriod?.[1]?.toISOString();
      //   return fetchJobs(allOptionsForQuery);
      default:
        // delete allOptionsForQuery.filters.entityType;
        allOptionsForQuery.startDate = currentPeriod?.[0]?.toISOString();
        allOptionsForQuery.endDate = currentPeriod?.[1]?.toISOString();
        return fetchEventsJobs(allOptionsForQuery);
    }
  };

  const eventFetchOnSuccessFunc = ({ data }) => {
    if (data) {
      setEvents?.(data);
      setEventsTableFilter?.(allOptions);
    }
  };

  const {
    data: payrollData,
    isLoading,
    refetch: refetchPayrollData,
  } = useQuery(["events", allOptions, currentPeriod], payrollFetchFunc, {
    onSuccess: eventFetchOnSuccessFunc,
  });

  const [calendarStartDate, calendarEndDate] = useMemo(() => {
    if (!calendarMiddleDate) return [null, null];
    const momentNow = moment(calendarMiddleDate);
    const momentStartOfMonth = momentNow.clone().startOf("month");
    const momentEndOfMonth = momentNow.clone().endOf("month");

    // if (userType === "User" && momentStartOfMonth.isBefore(moment().startOf("day"))) {
    //   momentStartOfMonth = moment().startOf("day");
    //   momentEndOfMonth = moment().endOf("month");
    // }

    const startDate = momentStartOfMonth.toISOString();
    const endDate = momentEndOfMonth.toISOString();
    return [startDate, endDate];
  }, [calendarMiddleDate]);

  const calendarOptions = { fetchAll: true };
  const allCalendarOptions = {
    ...calendarOptions,
    filters,
    venues,
    calendarStartDate,
    calendarEndDate,
  };

  const calendarEventFetchFunc = () => {
    const allOptionsForQuery = cloneDeep(allCalendarOptions);
    allOptionsForQuery.startDate = calendarStartDate;
    allOptionsForQuery.endDate = calendarEndDate;
    delete allOptionsForQuery.filters.timeFrame;
    return fetchEvents(allOptionsForQuery);
  };

  const {
    data: calendarEvents,
    isLoading: isLoadingCalendarEvents,
    refetch: refetchCalendarEvents,
  } = useQuery(["calendarevents", allCalendarOptions], calendarEventFetchFunc, {
    onSuccess: eventFetchOnSuccessFunc,
    enabled: calendarMiddleDate != null,
  });

  bindRefetchEvents?.(refetchPayrollData);
  const { action, eventUrl, jobSlug } = useParams();

  const sortByEventDate = (a, b) => {
    if (new Date(a.eventDate) > new Date(b.eventDate)) return 1;
    return -1;
  };

  const getAndSetFullEvent = useCallback(
    async (eid, url, updatePeriod) => {
      try {
        if (eid) {
          const ev = await fetchSingleEvent({ eventId: eid });
          if (ev) setCurrentEvent(ev);
          if (updatePeriod && ev.eventDate) {
            const todayMoment = moment(ev.eventDate);

            const newPeriod = getPeriodForDate(
              todayMoment.toISOString(),
              periodLength,
              shiftPeriodWeek,
              startOfPeriodDay
            );

            setCurrentPeriod(newPeriod);
          }
        } else if (url) {
          const ev = await fetchSingleEvent({ eventUrl: url });
          if (ev) setCurrentEvent(ev);
          if (updatePeriod && ev.eventDate) {
            const todayMoment = moment(ev.eventDate);

            const newPeriod = getPeriodForDate(
              todayMoment.toISOString(),
              periodLength,
              shiftPeriodWeek,
              startOfPeriodDay
            );

            setCurrentPeriod(newPeriod);
          }
        }
      } catch (error) {
        if (String(error).includes("401") || error?.response?.status === 401) {
          logoutAuth0User();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getAndSetFullJob = useCallback(
    async (jid, slug) => {
      try {
        if (jid) {
          const ev = await fetchSingleJob(null, jid);
          if (ev?.data?.job) setCurrentJob(ev?.data?.job);
        } else if (slug) {
          const ev = await fetchSingleJob(slug);
          if (ev?.data?.job) setCurrentJob(ev?.data?.job);
        }
      } catch (error) {
        if (String(error).includes("401") || error?.response?.status === 401) {
          logoutAuth0User();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!isLoading && eventUrl) {
      const ndx = payrollData?.data?.findIndex((item) => item.eventUrl === eventUrl);
      if (ndx > -1) {
        getAndSetFullEvent(payrollData.data[ndx]._id, true);
      } else {
        getAndSetFullEvent(null, eventUrl, true);
      }
    }
    if (!isLoading && jobSlug) {
      const ndx = payrollData?.data?.findIndex((item) => item.jobSlug === jobSlug);
      if (ndx > -1) {
        getAndSetFullJob(payrollData.data[ndx]._id);
      } else {
        getAndSetFullJob(null, jobSlug);
      }
    }
  }, [eventUrl, payrollData?.data, getAndSetFullEvent, getAndSetFullJob, isLoading, jobSlug]);

  useEffect(() => {
    if ((filters?.venueSlug || filters?.companySlug) && action) setPage(1);
    // causes interviews to misbehave, uncomment if needed
    // if (!filters?.timeFrame && !filters?.eventUrl && setFilters)
    //   setFilters((prev) => ({
    //     ...prev,
    //     eventType: prev.eventType || "Event",
    //   }));
  }, [filters?.venueSlug, action, filters?.companySlug]);

  function showSmallDate(date) {
    const opts = {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "numeric",
      minute: "numeric",
      seconds: "omit",
      hourCycle: "h12", // or 'h23' for 24-hour format
    };
    return date.toLocaleString("en-US", opts).replace(",", ""); // remove the comma separator
  }

  const columns = useMemo(
    () => [
      ...(company?.companyType === "Venue"
        ? [
            {
              title: "Logo",
              field: "logoUrl",
              customCell: (logoUrl, field, row) => (
                <VenueIcon
                  logoUrl={logoUrl}
                  slug={row?.venueSlug}
                  name={row?.venueName}
                  // goToEnabled={userType !== "User"}
                  goToEnabled
                />
              ),
            },
          ]
        : [
            {
              title: "Logo",
              field: "name",
              customCell: (_name, field, row) => (
                <CompanyLogoSquare company={row} defaultCompany={company} />
              ),
            },
            {
              title: "Customer",
              field: "companySlug",
              customCell: (slug) => <span>{slug?.toUpperCase()}</span>,
            },
          ]),
      ...(company?.companyType === "Venue" && (userType !== "User" || (!isMobile && !isMobileWidth))
        ? [
            {
              title: "Venue",
              field: "venueSlug",
              customCell: (slug) => <span>{slug?.toUpperCase()}</span>,
            },
          ]
        : []),
      // { title: "Event Url", field: "eventUrl" },
      {
        title: "Event/Job",
        field: "eventName",
        customCell: (eventName, field, row) => <span>{row?.eventName || row?.title}</span>,
      },
      {
        title: "Type",
        field: "eventName",
        customCell: (eventName, field, row) => <span>{row?.jobSlug ? "Shift Job" : "Event"}</span>,
      },
      {
        title: "Date",
        field: "eventDate",
        customCell: (date, field, row) =>
          isMobileWidth || isMobile ? (
            <span>{showSmallDate(new Date(date))}</span>
          ) : (
            <EventDate date={date} reportTimeTBD={row?.reportTimeTBD} showTime={false} />
          ),
      },
      ...(company?.companyType === "Venue"
        ? [
            {
              title: "Event Numbers",
              field: "positionsRequested",
              customCell: (requested, field, row) => {
                if (row?.eventUrl)
                  return (
                    <EventNumbers
                      setNavigateUrl={navigateWithBack}
                      eventUrl={row?.eventUrl}
                      requested={requested}
                      onRoster={row?.numberOnRoster || 0}
                      onWaitlist={row?.numberOnWaitlist || 0}
                      onRequest={row?.numberOnRequest || 0}
                      onPremise={row?.numberOnPremise || 0}
                      fontSize="medium"
                      isClient={userType === "Client"}
                    />
                  );
                return "";
              },
            },
          ]
        : []),
      {
        title: "Payroll Actions",
        field: "eventUrl",
        customCell: (url, field, row) => (
          <PayrollTableActions
            setNavigateUrl={navigateWithBack}
            setNavigateUrlToScreen={navigateToScreenWithBack}
            eventUrl={url}
            row={row}
            setFilters={setFilters}
            setActionSelected={setActionSelected}
            setOpen={setOpen}
            setEventPreview={setEventPreview}
            setJobPreview={setJobPreview}
            getAndSetFullEvent={getAndSetFullEvent}
            getAndSetFullJob={getAndSetFullJob}
            openTimeModal={() => {
              setCurrentEvent(row);
              toggleTimeModal(true);
            }}
          />
        ),
      },
    ],
    [
      company,
      userType,
      isMobile,
      isMobileWidth,
      navigateWithBack,
      navigateToScreenWithBack,
      setFilters,
      setActionSelected,
      setOpen,
      setEventPreview,
      setJobPreview,
      getAndSetFullEvent,
      getAndSetFullJob,
      setCurrentEvent,
    ]
  );

  const customerColumns = useMemo(
    () => [
      {
        title: "Logo",
        field: "name",
        customCell: (_name, field, row) => (
          <CompanyLogoSquare company={row} defaultCompany={company} />
        ),
      },
      {
        title: "Customer ID",
        field: "slug",
        customCell: (slug) => <div>{slug?.toUpperCase()}</div>,
      },
      { title: "Customer Name", field: "name" },
      { title: "City", field: "city" },
      { title: "State", field: "state" },
    ],
    [company]
  );

  const venueColumns = useMemo(
    () => [
      {
        title: "Logo",
        field: "logoUrl",
        customCell: (logoUrl, field, row) => (
          <VenueIcon logoUrl={logoUrl} slug={row?.slug} name={row?.name} />
        ),
      },
      {
        title: "Venue",
        field: "slug",
        customCell: (slug) => <span>{slug?.toUpperCase()}</span>,
      },
      {
        title: "Venue Name",
        field: "name",
      },
      {
        title: "City",
        field: "city",
      },
      {
        title: "State",
        field: "state",
      },
      { title: "Status", field: "status" },
    ],
    []
  );

  const onRowClick = (row, e) => {
    if (!["Master", "Admin"].includes(userType)) return null;
    e.stopPropagation();
    // setCurrentEvent(row);
    if (row?.eventUrl) {
      getAndSetFullEvent(row._id);
    } else if (row?.jobSlug) {
      getAndSetFullJob(row._id);
    }

    const basePath = new URL(window.location).pathname;
    const nodes = basePath.split("/");
    const navigateFunc = navigateWithoutBack;
    if (row?.eventUrl) {
      navigateFunc(
        `/payroll/event/${row?.eventUrl}${navigateToAction ? `/action/${navigateToAction}` : ""}`
      );
    } else if (row?.jobSlug) {
      navigateFunc(
        `/payroll/job/${row?.jobSlug}${navigateToAction ? `/action/${navigateToAction}` : ""}`
      );
    }
    return true;
  };

  const handleViewClick = () =>
    setCalendarListView((prev) => (prev === "List" ? "Calendar" : "List"));

  const filteredData = useMemo(() => {
    const eventsData = payrollData?.data ? payrollData : { data: [], count: 0 };
    return eventsData;
  }, [payrollData]);

  useEffect(() => {
    if (refetchPayrollData) setRefetchEventsTable?.(refetchPayrollData);
  }, [refetchPayrollData]);

  const handleChangePeriod = (v) => {
    setCurrentPeriod(
      getPeriodForDate(v.toISOString(), periodLength, shiftPeriodWeek, startOfPeriodDay)
    );

    const params = [];
    if (filters.entityType) params.push(`entityType=${filters.entityType}`);
    if (filters.venueSlug) params.push(`venueSlug=${filters.venueSlug}`);
    if (filters.companySlug) params.push(`companySlug=${filters.companySlug}`);
    if (filters.eventUrl || filters.jobSlug) {
      const newFilt = { ...filters };
      delete newFilt.eventUrl;
      delete newFilt.jobSlug;
      setFilters(newFilt);
    }
    navigateWithoutBack(`/payroll${params.length ? `?${params.join("&")}` : ""}`);
  };

  return (
    <Card className={classes.box}>
      <Grid container spacing={3} alignItems="center" justifyContent="space-between" py={1} pr={2}>
        <Grid item xs={12} sTablet={5}>
          <MDBox display="flex" flexWrap="wrap">
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="4rem"
              height="4rem"
              variant="gradient"
              bgColor="success"
              color="white"
              borderRadius="xl"
              ml={3}
              mt={-6}
            >
              <Icon fontSize="large">request_quote</Icon>
            </MDBox>
            <MDTypography variant="h5" color="dark" sx={{ ml: 2 }}>
              {title} - {calendarListView} View{" "}
              {payrollData?.data?.length
                ? `- ${payrollData.data.length} ${
                    company?.companyType === "Venue" ? "Event" : "Interview"
                  }${payrollData.data?.length > 1 ? "s" : ""}`
                : ""}
            </MDTypography>
            <MDBox ml={3}>
              {showFiltersList && (
                // && userType !== "User"
                <Grid item xs={12}>
                  <FiltersList filters={filters} setFilters={setFilters} />
                </Grid>
              )}
            </MDBox>
          </MDBox>
        </Grid>
        <Grid item xs={12} sTablet={7} alignItems="center">
          <Grid container direction="row" alignItems="flex-end" justifyContent="space-between">
            <Grid item sm={11.5}>
              <Grid container spacing={2}>
                <Grid item xl={6} minWidth="500px" alignItems="center">
                  <Stack direction="row" alignItems="center">
                    <MDTypography variant="h5" color="dark" sx={{ ml: 2 }}>
                      Period:
                    </MDTypography>
                    <DatePeriodSelector value={currentPeriod} onChange={handleChangePeriod} />
                  </Stack>
                </Grid>
                <Grid container item minWidth="500px" xs={12} xl={6}>
                  <Grid item xs={6}>
                    <Searchbar
                      fetch={(params) =>
                        fetchEventsJobs({
                          ...params,
                          startDate: currentPeriod?.[0]?.toISOString(),
                          endDate: currentPeriod?.[1]?.toISOString(),
                        })
                      }
                      fetchAll={false}
                      placeholder={
                        company?.companyType === "Venue" ? "Search Event/Job" : "Search Job"
                      }
                      columns={columns.slice(1, columns.length - 1)}
                      queryCharacterLimit={4}
                      resultsLimit={25}
                      setPage={setPage}
                      searchBy={["eventUrl", "eventName", "title", "jobSlug"]}
                      noFilter
                      idField="_id"
                      scrollLimit={200}
                      onRowClick={(entity) => {
                        if (entity?.eventUrl) {
                          setCurrentJob(null);
                          navigate(`/payroll/event/${entity?.eventUrl}/action/eventsinfo`);
                        } else if (entity?.jobSlug) {
                          setCurrentEvent(null);
                          navigate(`/payroll/job/${entity?.jobSlug}/action/jobinfo`);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    {company?.companyType === "Venue" ? (
                      <Searchbar
                        fetch={fetchVenues}
                        fetchAll={false}
                        placeholder="Search Venue/City"
                        columns={venueColumns}
                        queryCharacterLimit={2}
                        resultsLimit={10}
                        setFilters={setFilters}
                        setPage={setPage}
                        searchBy={["slug", "name", "city"]}
                        filterBy="venueSlug"
                        filterByColumn="slug"
                        idField="slug"
                        onRowClick={handleSearch}
                        // returnAfterClick={userType === "User"}
                        returnAfterClick={false}
                        // filterData={
                        //   userType === "User"
                        //     ? (item) => {
                        //         return item.status === "Active" && !lockedVenues.includes(item.slug);
                        //       }
                        //     :
                        //     undefined
                        // }
                      />
                    ) : (
                      <Searchbar
                        fetch={fetchCustomers}
                        fetchAll={false}
                        placeholder="Search Customers"
                        columns={customerColumns}
                        queryCharacterLimit={2}
                        resultsLimit={10}
                        setFilters={setFilters}
                        setPage={setPage}
                        searchBy={["slug", "name", "city"]}
                        filterBy="companySlug"
                        filterByColumn="slug"
                        idField="slug"
                        onRowClick={handleSearch}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <MDBox pt={2} pb={2}>
        {calendarListView === "Calendar" ? (
          <Grid container alignItems="center">
            <Grid item xs={12} style={{ maxHeight: 780, overflow: "scroll" }}>
              <EventCalendar
                events={calendarEvents}
                isLoading={isLoadingCalendarEvents}
                sortByEventDate={sortByEventDate}
                calendarListView={calendarListView}
                onSetMiddleDate={setCalendarMiddleDate}
                navigateWithBack={navigateWithBack}
              />
            </Grid>
          </Grid>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={filteredData}
              onRowClick={(row, e) => {
                onRowClick(row, e);
              }}
              page={page}
              limit={limit}
              setPage={setPage}
              setLimit={setLimit}
              order={order}
              orderBy={orderBy}
              toggleSort={toggleSort}
              defaultSort={defaultSort}
              fetchAll={fetchAll}
              isLoading={isLoading} // Extract isLoading from useQuery then pass here (required)
              greybar
              divider
              idField="_id"
            />
          </>
        )}
      </MDBox>
      {isTimeModalOpen && (
        <TimeInOutModal
          isOpen={isTimeModalOpen}
          toggleOpen={toggleTimeModal}
          event={currentEvent}
        />
      )}
      {isPayrollModalOpen && (
        <PayrollModal isOpen={isPayrollModalOpen} toggleOpen={togglePayrollModal} />
      )}
      {pageLimitConfirmationModal}
    </Card>
  );
};
PayrollTable.defaultProps = {
  title: "Payroll",
  showFiltersList: true,
};

// Typechecking props
PayrollTable.propTypes = {
  title: PropTypes.string,
  showFiltersList: PropTypes.bool,
};

export default PayrollTable;
